import React from 'react';

export function buildSpamScorePreview({ parsed, body, htmlPart, attachments }) {
  const headers = parsed?.headers || {};
  const subject = headers.subject || '';
  const from = headers.from || '';
  const authResults = headers['authentication-results'] || '';
  const receivedSpf = headers['received-spf'] || '';
  const lowerBody = (body || '').toLowerCase();
  const lowerHtml = (htmlPart || '').toLowerCase();
  const reasons = [];
  let score = 0;

  const addReason = (points, label) => {
    score += points;
    reasons.push({ points, label });
  };

  if (!from.includes('@')) addReason(18, 'Sender header is missing a valid email address.');
  if (!subject.trim()) addReason(10, 'Message has no subject line.');
  if (subject === subject.toUpperCase() && subject.trim().length >= 8) addReason(12, 'Subject uses all caps.');
  if ((subject.match(/!/g) || []).length >= 3) addReason(8, 'Subject uses excessive exclamation marks.');
  if (/(urgent|act now|free money|winner|limited time|verify your account|click here)/i.test(subject)) addReason(15, 'Subject contains common spam phrases.');
  if (/(bitcoin|crypto|gift card|wire transfer|password|login immediately|verify your account|bank account)/i.test(lowerBody)) addReason(14, 'Body contains suspicious financial or credential bait keywords.');
  if (/(https?:\/\/|www\.)/i.test(lowerBody) && /(urgent|verify|reset|login|click)/i.test(lowerBody)) addReason(10, 'Body mixes links with urgency or account prompts.');
  if (attachments?.length > 0) addReason(Math.min(attachments.length * 4, 12), 'Message includes attachments.');
  if (lowerHtml.includes('<form')) addReason(12, 'HTML contains a form element.');
  if (lowerHtml.includes('display:none') || lowerHtml.includes('visibility:hidden')) addReason(8, 'HTML contains hidden content.');
  if (authResults && !/spf=pass|dkim=pass|dmarc=pass/i.test(authResults)) addReason(10, 'Authentication results do not show a clear SPF, DKIM, or DMARC pass.');
  if (receivedSpf && !/pass/i.test(receivedSpf)) addReason(10, 'SPF check is not marked as pass.');
  if (!authResults && !receivedSpf) addReason(6, 'No authentication headers were detected.');

  score = Math.min(score, 100);

  let verdict = 'Very Good';
  let tone = 'good';
  if (score >= 80) {
    verdict = 'High Risk';
    tone = 'danger';
  } else if (score >= 60) {
    verdict = 'Suspicious';
    tone = 'warning';
  } else if (score >= 40) {
    verdict = 'Fair';
    tone = 'warning';
  } else if (score >= 20) {
    verdict = 'Good';
    tone = 'good';
  }

  return {
    score,
    verdict,
    tone,
    reasons: reasons.length ? reasons : [{ points: 0, label: 'No obvious spam indicators were detected by the local heuristic.' }]
  };
}

export default function SpamScorePreview({ preview, C }) {
  const toneColor = preview?.tone === 'danger' ? C.danger : preview?.tone === 'warning' ? '#f59e0b' : C.green;

  if (!preview) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Score</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: toneColor }}>{preview.score}<span style={{ fontSize: 14, color: C.muted, marginLeft: 6 }}>/ 100</span></div>
        </div>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Verdict</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: toneColor }}>{preview.verdict}</div>
        </div>
        <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Signals</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{preview.reasons.length}</div>
        </div>
      </div>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Heuristic breakdown</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {preview.reasons.map((reason, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, paddingBottom: 10, borderBottom: index === preview.reasons.length - 1 ? 'none' : `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, color: C.subtle, lineHeight: 1.6 }}>{reason.label}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: reason.points > 0 ? C.danger : C.muted, whiteSpace: 'nowrap' }}>{reason.points > 0 ? `+${reason.points}` : '0'}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.7 }}>
        This preview uses local rules from the visible message content and headers. It is a fast estimate, not a replacement for a real spam filtering engine.
      </div>
    </div>
  );
}